export type Comparator<T> = (a: T, b: T) => -1 | 0 | 1;

/**
 * Generate a comparator from a mapping function.
 *
 * @example
 * // Returns a comparator that compares items by primary stat
 * compareBy((item) => item.primStat.value)
 */
export function compareBy<T, V>(fn: (arg: T) => V): Comparator<T> {
  return (a, b) => {
    const aVal = fn(a);
    const bVal = fn(b);
    // Undefined is neither greater than or less than anything. This considers it less than everything.
    return aVal === undefined
      ? bVal === undefined
        ? 0
        : -1
      : bVal === undefined
      ? 1
      : aVal < bVal
      ? -1
      : aVal > bVal
      ? 1
      : 0;
  };
}

/**
 * Reverse the order of a comparator.
 */
export function reverseComparator<T>(compare: Comparator<T>): Comparator<T> {
  return (a, b) => compare(b, a);
}

/**
 * Chain multiple comparators together. If two values are equal according to one comparator, we try the next and so on.
 */
export function chainComparator<T>(...compares: Comparator<T>[]): Comparator<T> {
  return (a, b) => {
    for (const compare of compares) {
      const retval = compare(a, b);
      if (retval !== 0) {
        return retval;
      }
    }
    return 0;
  };
}
